import type { ConversationClient } from '@/types/conversations'
import type { BetterplaceListing } from '@/services/betterplaceService'
import type { Mail, WhatsAppLine } from './data'
import type { WhatsAppLinePayload, WhatsAppThreadPayload } from '@/services/conversationsApi'

/** La linea tal como se pinta: sin nombre puesto, manda el numero. */
function toWhatsAppLine(payload?: WhatsAppLinePayload | null): WhatsAppLine | undefined {
  if (!payload || !payload.phone_number_id) return undefined
  return {
    phoneNumberId: payload.phone_number_id,
    label: payload.display_name?.trim() || payload.display_phone_number?.trim() || payload.phone_number_id,
    isInternal: Boolean(payload.is_internal),
  }
}
import i18n from '@/lib/i18n'

export type { Mail }

interface PhoneCall {
  id: string
  call_type: 'telnyx' | 'elevenlabs' | 'hybrid' | 'unknown'
  external_id: string
  caller: {
    id: string
    phone_number: string
    name: string
    is_verified: boolean
    tags?: string[]
  }
  agent: {
    id: string
    agent_id: string
    name: string
    is_active: boolean
  }
  status: 'initiated' | 'ringing' | 'answered' | 'bridged' | 'completed' | 'failed' | 'hangup'
  duration_seconds: number | null
  cost: number | null
  summary: string | null
  intent: Record<string, unknown> | null
  successful: boolean | null
  turns_count: number
  started_at: string
  answered_at: string | null
  ended_at: string | null
  feedback: string
  archived: boolean
}

interface Conversation {
  id: string;
  user: string;
  client?: ConversationClient | unknown;
  user_display_name: string;
  title: string;
  description?: string;
  started_at: string;
  source: string;
  platform?: string;
  feedback?: string;
  channel_user_id?: string;
  channel_conversation_id?: string;
  status: string;
  metadata?: Record<string, unknown> & {
    source?: string;
  };
  timestamp: string;
  message_count: number;
  last_message?: {
    text: string;
    sender: string;
    timestamp: string;
    metadata?: {
      property_id?: string;
      source?: string;
    };
  };
  unread_count?: number;
  // Estos tres ya se leian aqui sin estar declarados: TypeScript no lo veia
  // porque esta interfaz es local y no la de `conversationsApi`.
  can_reply?: boolean;
  reply_block_reason?: string | null;
  phone_number_id?: string;
  line?: WhatsAppLinePayload | null;
  whatsapp_threads?: WhatsAppThreadPayload[] | null;
  user_tags?: Array<{
    id?: string;
    name?: string | null;
    color?: string;
    description?: string;
    tag_type?: string;
  }>;
  conversation_tags?: Array<{
    id?: string;
    name?: string | null;
    color?: string;
    description?: string;
    tag_type?: string;
  }>;
}

function nombresDeTags(tags: unknown): string[] {
  return Array.isArray(tags)
    ? (tags as Array<{ name?: string | null }>)
        .map((tag) => (tag && typeof tag.name === 'string' ? tag.name.trim() : ''))
        .filter(Boolean)
    : []
}

// El orden es la correccion: los de la conversacion mandan sobre los de la
// persona. `user_tags` dice que le interesa a alguien -y se acumula con los
// meses-, no de que trata esta conversacion. Se mantiene detras porque hasta
// que el historico este retrollenado hay conversaciones sin tags propios.
function extractConversationTagNames(conversation: Conversation): string[] {
  const conversationTags = nombresDeTags(conversation.conversation_tags)
  if (conversationTags.length > 0) {
    return conversationTags
  }

  const userTags = nombresDeTags(conversation.user_tags)
  if (userTags.length > 0) {
    return userTags
  }

  return Array.isArray(conversation.metadata?.tags)
    ? conversation.metadata.tags.filter((tag): tag is string => typeof tag === 'string' && tag.trim().length > 0)
    : []
}

function getRelativeTimeLocale(): 'en' | 'es' {
  const language = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase()
  return language.startsWith('es') ? 'es' : 'en'
}

function formatRelativeDisplayDate(activityDateInput: string): string {
  const activityDate = new Date(activityDateInput)
  const activityTime = activityDate.getTime()

  if (Number.isNaN(activityTime)) {
    return ''
  }

  const locale = getRelativeTimeLocale()
  const now = Date.now()
  const diffInHours = Math.floor(Math.max(0, now - activityTime) / (1000 * 60 * 60))

  if (diffInHours < 1) {
    return locale === 'es' ? 'Hace menos de 1 hora' : 'Less than 1 hour ago'
  }

  const relativeFormatter = new Intl.RelativeTimeFormat(locale, { numeric: 'always' })

  if (diffInHours < 24) {
    const label = relativeFormatter.format(-diffInHours, 'hour')
    return label.charAt(0).toUpperCase() + label.slice(1)
  }

  const diffInDays = Math.floor(diffInHours / 24)
  const label = relativeFormatter.format(-diffInDays, 'day')
  return label.charAt(0).toUpperCase() + label.slice(1)
}

export function transformConversationToMail(conversation: Conversation): Mail {

  // Get the last message for preview
  const lastMessage = conversation.last_message?.text || conversation.description || 'No messages yet'
  const lastMessageTimestamp = conversation.last_message?.timestamp
  const lastActivity = lastMessageTimestamp || conversation.timestamp || conversation.started_at


  const displayDate = formatRelativeDisplayDate(lastActivity)

  // Map API status to frontend status (using backend-aligned values)
  const statusMap: Record<string, Mail['status']> = {
    'active': 'active',
    'needs_review': 'needs_review',
    'completed': 'completed',
    'archived': 'archived',
    'ai_responding': 'ai_responding'
  }

  // Betterplace conversations are persisted as source=Other plus metadata.channel_type.
  const metadataChannelType =
    typeof conversation.metadata?.channel_type === 'string'
      ? conversation.metadata.channel_type
      : ''
  const metadataSource =
    typeof conversation.metadata?.source === 'string'
      ? conversation.metadata.source
      : ''
  const rawSource =
    metadataChannelType ||
    conversation.platform ||
    (conversation.source === 'Other' ? metadataSource : conversation.source) ||
    metadataSource ||
    ''
  // Preserve the most specific channel key for registry-driven inbox filters.
  // Some API-only / special channels may arrive with auxiliary metadata even
  // when the persisted `source` is generic or missing.
  const sourceKey = String(
    (conversation.source && conversation.source !== 'Other'
      ? conversation.source
      : rawSource) || conversation.source || '',
  ).trim()
  const normalizedSource = rawSource.toString().toLowerCase().trim()
  const platformMap: Record<string, Mail['platform']> = {
    'betterplace': 'betterplace',
    'betterplace_api': 'betterplace',
    'whatsapp': 'whatsapp',
    'wa': 'whatsapp',
    'whatsapp_business': 'whatsapp',
    'wp_whatsapp': 'whatsapp',
    'instagram': 'instagram',
    'ig': 'instagram',
    'instagram_dm': 'instagram',
    'facebook': 'facebook',
    'messenger': 'facebook',
    'facebook messenger': 'facebook',
    'fb': 'facebook',
    'web': 'web',
    'website': 'web',
    'web_chat': 'web',
    'webchat': 'web',
    'wordpress': 'wordpress',
    'wp': 'wordpress',
    'twitter': 'twitter',
    'other': 'other',
    'phone call': 'phone_call',
    'phone_call': 'phone_call',
    'phonecall': 'phone_call',
    'call': 'phone_call',
    'phone': 'phone_call',
    'telnyx': 'phone_call',
    'avatar': 'avatar',
    'liveavatar': 'avatar',
    'heygen': 'avatar',
  }
  const mappedPlatform = platformMap[normalizedSource] || 'other'

  const intentStatus = conversation.metadata?.intent_status
  // Consider analyzing only when backend marks it as analyzing
  const isAnalyzing = intentStatus === 'analyzing'

  const rawClient = conversation.client
  const client =
    rawClient && typeof rawClient === 'object'
      ? (rawClient as ConversationClient)
      : undefined

  const profilePicUrl = client?.profile_pic_url?.trim() || undefined
  const emailFromClient = client?.email?.trim() || ''
  const phoneFromClient = client?.phone?.trim() || ''
  const propertyIdFromConversation =
    typeof conversation.metadata?.property_id === 'string'
      ? conversation.metadata.property_id.trim()
      : ''
  const propertyIdFromLastMessage =
    typeof conversation.last_message?.metadata?.property_id === 'string'
      ? conversation.last_message.metadata.property_id.trim()
      : ''
  const propertyId = propertyIdFromConversation || propertyIdFromLastMessage || undefined

  const line = toWhatsAppLine(conversation.line)
  const whatsappThreads = (conversation.whatsapp_threads || []).map((thread: WhatsAppThreadPayload) => ({
    id: thread.id,
    phoneNumberId: thread.phone_number_id,
    line: toWhatsAppLine(thread.line),
    status: (statusMap[thread.status] || 'active') as Mail['status'],
    unreadCount: thread.unread_count ?? 0,
    canReply: thread.can_reply ?? true,
  }))

  return {
    id: conversation.id,
    user: conversation.user, // User ID for tag operations
    name: conversation.user_display_name || 'Unknown User',
    email: emailFromClient,
    phone: phoneFromClient,
    subject: conversation.title || 'Sin título',
    text: lastMessage,
    date: lastActivity,
    lastActivity,
    displayDate,
    status: statusMap[conversation.status] || 'active',
    platform: mappedPlatform,
    isAnalyzing,
    category: conversation.description ? 'Consulta' : 'General',
    tags: extractConversationTagNames(conversation),
    feedback: conversation.feedback || '',
    unreadCount: conversation.unread_count || 0,
    canReply: conversation.can_reply ?? true,
    replyBlockReason: conversation.reply_block_reason?.trim() || undefined,
    profilePicUrl,
    sourceKey,
    propertyId,
    phoneNumberId: conversation.phone_number_id || undefined,
    line,
    whatsappThreads,
  }
}

export function transformConversationsToMails(conversations: Conversation[]): Mail[] {
  return conversations.map(transformConversationToMail)
}

function getBetterplaceListingIdentifier(listing: BetterplaceListing, fallbackIndex: number): string {
  const sourceKey =
    listing.source && typeof listing.source === 'object' && typeof listing.source.sourceKey === 'string'
      ? listing.source.sourceKey
      : undefined
  const candidates = [listing.property_id, listing.id, sourceKey, listing.externalId, listing.ref]

  for (const candidate of candidates) {
    if (candidate == null) continue
    const value = String(candidate).trim()
    if (value) return value
  }

  return `listing-${fallbackIndex + 1}`
}

function getBetterplaceListingDate(listing: BetterplaceListing): string {
  if (typeof listing.changed_at === 'number' && Number.isFinite(listing.changed_at)) {
    const changedAt = new Date(listing.changed_at)
    if (!Number.isNaN(changedAt.getTime())) {
      return changedAt.toISOString()
    }
  }

  return new Date().toISOString()
}

function getBetterplaceListingText(listing: BetterplaceListing): string {
  const description = typeof listing.description === 'string' ? listing.description.trim() : ''
  if (description) {
    return description.length > 180 ? `${description.slice(0, 177)}...` : description
  }

  const location = [listing.location?.city, listing.location?.province]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .join(' · ')

  const price = Number(listing.sale_price ?? listing.price ?? 0)
  const detailParts = [
    location,
    price > 0 ? `${price.toLocaleString('es-ES')} €` : '',
  ].filter(Boolean)

  return detailParts.join(' · ') || 'Propiedad sincronizada desde Betterplace.'
}

export function transformBetterplaceListingToMail(
  listing: BetterplaceListing,
  fallbackIndex = 0,
): Mail {
  const propertyId = getBetterplaceListingIdentifier(listing, fallbackIndex)
  const lastActivity = getBetterplaceListingDate(listing)
  const displayDate = formatRelativeDisplayDate(lastActivity)
  const title = typeof listing.title === 'string' && listing.title.trim()
    ? listing.title.trim()
    : `Propiedad Betterplace ${propertyId}`
  const phone = Array.isArray(listing.contacts)
    ? listing.contacts.find((value): value is string => typeof value === 'string' && value.trim().length > 0)
    : undefined

  const sourceLabel = typeof listing.source === 'string' ? listing.source : 'Betterplace'

  return {
    id: `betterplace:${propertyId}`,
    user: `betterplace:${propertyId}`,
    name: listing.location?.city || sourceLabel || 'Betterplace',
    email: '',
    phone,
    subject: title,
    text: getBetterplaceListingText(listing),
    date: lastActivity,
    lastActivity,
    displayDate,
    status: 'active',
    platform: 'betterplace',
    isAnalyzing: false,
    category: 'Betterplace',
    tags: [],
    unreadCount: 0,
    sourceKey: 'Betterplace',
    propertyId,
  }
}

export function transformBetterplaceListingsToMails(listings: BetterplaceListing[]): Mail[] {
  return listings.map((listing, index) => transformBetterplaceListingToMail(listing, index))
}

export function getChannelFilterOptions() {
  return [
    { value: 'all', label: 'Todos los canales' },
    { value: 'Betterplace', label: 'Betterplace' },
    { value: 'WhatsApp', label: 'WhatsApp' },
    { value: 'Instagram', label: 'Instagram' },
    { value: 'Facebook', label: 'Facebook' },
    { value: 'WordPress', label: 'WordPress' },
    { value: 'Other', label: 'Otros' }
  ]
}

export function getStatusFilterOptions() {
  return [
    { value: 'all', label: 'Todos los estados' },
    { value: 'active', label: 'Activas' },
    { value: 'pending', label: 'Pendientes' },
    { value: 'needs_review', label: 'Necesitan revisión' },
    { value: 'completed', label: 'Completadas' },
    { value: 'archived', label: 'Archivadas' }
  ]
}

export function transformPhoneCallToMail(phoneCall: PhoneCall): Mail {
  // Get the summary or create a default message
  const lastMessage = phoneCall.summary || `Llamada telefónica - ${phoneCall.turns_count} intercambios`
  const lastActivity = phoneCall.ended_at || phoneCall.started_at

  const displayDate = formatRelativeDisplayDate(lastActivity)

  // Map phone call status to conversation status
  // If archived, always show as archived regardless of call status
  if (phoneCall.archived) {
    return {
      id: phoneCall.id,
      user: phoneCall.caller.id,
      name: phoneCall.caller.phone_number,
      email: '',
      phone: phoneCall.caller.phone_number,
      subject: `Llamada telefónica - ${phoneCall.caller.phone_number}`,
      text: lastMessage,
      date: lastActivity,
      lastActivity,
      displayDate,
      status: 'archived',
      platform: 'phone_call',
      isAnalyzing: false,
      category: 'Llamada telefónica',
      tags: [],
      feedback: phoneCall.feedback || '',
      unreadCount: 0,
      sourceKey: 'Phone Call'
    }
  }

  const statusMap: Record<string, Mail['status']> = {
    'initiated': 'active',
    'ringing': 'active',
    'answered': 'active',
    'bridged': 'active',
    'completed': 'completed',
    'failed': 'needs_review',
    'hangup': 'completed'
  }

  // Create subject based on call info
  const subject = `Llamada telefónica - ${phoneCall.caller.phone_number}`

  return {
    id: phoneCall.id,
    user: phoneCall.caller.id,
    name: phoneCall.caller.phone_number,
    email: '',
    phone: phoneCall.caller.phone_number,
    subject,
    text: lastMessage,
    date: lastActivity,
    lastActivity,
    displayDate,
    status: statusMap[phoneCall.status] || 'active',
    platform: 'phone_call',
    isAnalyzing: false,
    category: 'Llamada telefónica',
    tags: phoneCall.caller.tags || [],
    feedback: phoneCall.feedback || '',
    unreadCount: 0,
    sourceKey: 'Phone Call'
  }
}

export function transformPhoneCallsToMails(phoneCalls: PhoneCall[]): Mail[] {
  return phoneCalls.map(transformPhoneCallToMail)
}
